import { useMemo, useState } from "react";
import { ExpandedState, SortingState } from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";
import { type SxProps, type Theme } from "@mui/material/styles";

import FiltersOnCreativeTable from "containers/admin/clients/campaign/components/filters-on-creative-table/filters-on-creative-table";
import {
  DataGrid,
  TableLoader,
  TableSearchAndFilters,
  TableContainer,
  TableFillHeightBody,
  TableItemsNotFound,
  type EmptyGroupMeta,
  shouldPinPrimaryColumnLeft,
} from "ui/data-grid";
import FolderTableRow from "containers/admin/clients/campaign/campaign-creative-page/components/folder-table-row/folder-table-row";
import { Button } from "ui/button";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";

import { hasCampaignTemplateInstance } from "containers/admin/clients/campaign/campaign-template-instance.utils";
import { createCreactiveTableColumns } from "containers/admin/clients/campaign/campaign-creative-page/components/creactive-table/creactive-table-columns";
import { CreativeTableSelectionContext } from "containers/admin/clients/campaign/campaign-creative-page/components/creactive-table/creative-table-selection-context";
import { useEmailWriterPollingSessionActive } from "hooks/use-email-writer-polling-session-active";

import { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { hasPermission } from "utilities/user";
import { Permission } from "types/auth";

import {
  useGetTouchpointVersionStatusesQuery,
  type TouchpointStatusDictionaryItem,
} from "hooks/api/dictionary";
import { useTouchpoints } from "hooks/api/use-touchpoints";
import { useFolders } from "hooks/api/use-folders";
import { useFeatureFlag } from "contexts/feature-flag-context";
import { FiltersToApplyOnCreativeTable } from "containers/admin/clients/campaign/components/filters-on-creative-table/filters-on-creative-table.types";
import { CampaignFolderDTO } from "generated/schemas";
import type {
  GetAllTouchpointsForCampaignClassTypeInItem,
  GetAllTouchpointsForCampaignContentTypeInItem,
  GetAllTouchpointsForCampaignParams,
  GetAllTouchpointsForCampaignPopulationTypeInItem,
  GetAllTouchpointsForCampaignTypeInItem,
} from "generated/schemas";
import {
  DEFAULT_FILTERS_TO_APPLY,
  FILTER_ID_TO_TITLE,
} from "containers/admin/clients/campaign/components/filters-on-creative-table/filters-on-creative-table.consts";
import { useUserLayout, useDebouncedUpdateUserLayout } from "hooks/api/use-user-layout";
import type { ColumnOption } from "types/table";

import Icons from "assets";

const TABLE_CONFIG_KEY = "campaignCreatives";

const EXPAND_ALL_BUTTON_SX: SxProps<Theme> = { mb: -0.75 };

const PRIMARY_COLUMN_IDS = ["folderId", "checkbox", "warningIcon", "name", "actions"];

// Module-scope so the columns keep their identity while the dictionary is still loading.
const NO_VERSION_STATUSES: TouchpointStatusDictionaryItem[] = [];

export interface TouchpointWithFolder extends TouchpointAttributes {
  folder?: CampaignFolderDTO;
}

interface CreativeTableProps {
  selectedIds: string[];
  setSelectedIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  folderId?: string;
  actionSlot?: React.ReactNode;
}

const CreativeTable = ({
  selectedIds,
  setSelectedIds,
  folderId,
  actionSlot,
}: CreativeTableProps) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentUser = useCurrentUser();

  const isClientUser = useMemo(
    () => hasPermission(currentUser, Permission.PERM_CLIENT_USER),
    [currentUser],
  );
  const isBatchManagementEnabled = useFeatureFlag("batch-management");

  const [expandedFolderIds, setExpandedFolderIds] = useState<ExpandedState>(true);

  const { data: touchpointVersionStatuses = NO_VERSION_STATUSES } =
    useGetTouchpointVersionStatusesQuery();

  const { data: tableColumnsConfig = {} } = useUserLayout();
  const { toggleColumnVisibility } = useDebouncedUpdateUserLayout();

  const handleToggleColumn = (columnId: string) => {
    toggleColumnVisibility(TABLE_CONFIG_KEY, columnId);
  };

  const [sorting, setSorting] = useState<SortingState>([{ id: "lastModifiedDate", desc: true }]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersToApply, setFiltersToApply] =
    useState<FiltersToApplyOnCreativeTable>(DEFAULT_FILTERS_TO_APPLY);

  const queryParams = useMemo<GetAllTouchpointsForCampaignParams>(() => {
    const params: GetAllTouchpointsForCampaignParams = {
      sort: sorting.length ? sorting.map((s) => `${s.id},${s.desc ? "desc" : "asc"}`) : undefined,
      q: searchTerm || undefined,
    };

    if (filtersToApply.type?.length) {
      params["type.in"] = filtersToApply.type as GetAllTouchpointsForCampaignTypeInItem[];
    }

    if (filtersToApply.contentType?.length) {
      params["contentType.in"] =
        filtersToApply.contentType as GetAllTouchpointsForCampaignContentTypeInItem[];
    }

    if (filtersToApply.populationType?.length) {
      params["populationType.in"] =
        filtersToApply.populationType as GetAllTouchpointsForCampaignPopulationTypeInItem[];
    }

    if (filtersToApply.classType?.length) {
      params["classType.in"] =
        filtersToApply.classType as GetAllTouchpointsForCampaignClassTypeInItem[];
    }

    if (folderId) {
      params["folderId.in"] = [folderId];
    }

    return params;
  }, [
    sorting,
    searchTerm,
    filtersToApply.type,
    filtersToApply.contentType,
    filtersToApply.populationType,
    filtersToApply.classType,
    folderId,
  ]);

  const {
    data,
    isLoading: isTouchpointsLoading,
    isFetching: isTouchpointsFetching,
  } = useTouchpoints(queryParams, { placeholderData: keepPreviousData });

  const {
    data: foldersResponse,
    isLoading: isFoldersLoading,
    isFetching: isFoldersFetching,
  } = useFolders(
    { unpaged: true, sort: ["name,asc"] },
    { enabled: !folderId, placeholderData: keepPreviousData },
  );

  const { data: unfilteredTouchpointsData } = useTouchpoints(
    { size: 0 },
    { enabled: !folderId, placeholderData: keepPreviousData },
  );

  const isInitialLoading =
    (isTouchpointsLoading && !data) || (!folderId && isFoldersLoading && !foldersResponse);
  const isAnyFetching = isTouchpointsFetching || isFoldersFetching;

  const folders = foldersResponse?.content ?? [];

  // Total for "Not in a Folder" group
  const totalNotInFolder = useMemo(() => {
    if (folderId) return undefined;
    const totalCampaign = unfilteredTouchpointsData?.totalElements ?? 0;
    const totalInFolders = (folders ?? []).reduce((sum, f) => sum + (f.touchpointCount ?? 0), 0);
    const notInFolder = totalCampaign - totalInFolders;
    return notInFolder >= 0 ? notInFolder : undefined;
  }, [unfilteredTouchpointsData?.totalElements, folders, folderId]);

  const touchpoints = useMemo<TouchpointWithFolder[]>(() => {
    const items = data?.items ?? [];
    return items.map((item) => ({
      ...item,
      folder: (folders ?? []).find((f) => f.id === item.folderId),
    }));
  }, [data?.items, folders]);

  const touchpointsForTable = useMemo<TouchpointWithFolder[]>(() => {
    if (folderId) return touchpoints;
    return [
      ...touchpoints.filter((tp) => tp.folderId),
      ...touchpoints.filter((tp) => !tp.folderId),
    ];
  }, [touchpoints, folderId]);

  const totalRecords = data?.totalElements ?? 0;

  const isCurrentPageAllSelected = useMemo(() => {
    if (touchpoints.length === 0) return false;
    return touchpoints.every((tp) => selectedIds.includes(tp.id));
  }, [touchpoints, selectedIds]);

  const selection = useMemo(
    () => ({
      selectedIds,
      setSelectedIds,
      displayedTouchpoints: touchpoints,
      isCurrentPageAllSelected,
    }),
    [selectedIds, setSelectedIds, touchpoints, isCurrentPageAllSelected],
  );

  const isTemplateCampaign = hasCampaignTemplateInstance(currentCampaign);
  const isEmailWriterSessionActive = useEmailWriterPollingSessionActive(
    currentCampaign.id,
    currentClient.id,
  );

  const allColumns = useMemo(
    () =>
      createCreactiveTableColumns<TouchpointWithFolder>({
        clientId: currentClient.id,
        campaignId: currentCampaign.id,
        touchpointVersionStatuses,
        isClientUser,
        isTemplateCampaign,
        isEmailWriterSessionActive,
        isBatchManagementEnabled,
        folderId,
      }),
    [
      currentClient.id,
      currentCampaign.id,
      touchpointVersionStatuses,
      isClientUser,
      isTemplateCampaign,
      isEmailWriterSessionActive,
      isBatchManagementEnabled,
      folderId,
    ],
  );

  const columnOptions = useMemo<ColumnOption[]>(
    () =>
      allColumns
        .filter(
          (col): col is typeof col & { id: string; header: string } =>
            typeof col.header === "string" && col.id !== undefined && col.id !== "name" && col.id !== "actions",
        )
        .map((col) => ({ id: col.id, label: col.header })),
    [allColumns],
  );

  const hiddenColumnIds = useMemo(
    () => new Set(tableColumnsConfig[TABLE_CONFIG_KEY]?.hidden ?? []),
    [tableColumnsConfig],
  );

  const columns = useMemo(
    () => allColumns.filter((col) => !hiddenColumnIds.has(col.id ?? "")),
    [allColumns, hiddenColumnIds],
  );

  const pinPrimaryColumnLeft = shouldPinPrimaryColumnLeft(columns, PRIMARY_COLUMN_IDS);

  const emptyFolders = useMemo((): EmptyGroupMeta<{ folder: CampaignFolderDTO }>[] => {
    if (folderId) return [];
    const folders = foldersResponse?.content ?? [];
    const isFiltered = !!(
      filtersToApply?.type?.length ||
      filtersToApply?.contentType?.length ||
      filtersToApply?.populationType?.length ||
      filtersToApply?.classType?.length
    );

    return folders
      .filter((f): f is CampaignFolderDTO & { id: string } => Boolean(f.id))
      .filter((f) => !touchpoints.some((tp) => tp.folderId === f.id))
      .filter((f) => {
        if (!searchTerm) return true;
        const name = f.name ?? "";
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .filter(() => !isFiltered)
      .map((folder) => ({
        id: `empty-${folder.id}`,
        groupValue: folder.id,
        original: { folder },
      }));
  }, [foldersResponse, touchpoints, searchTerm, filtersToApply, folderId]);

  const isSearchOrFiltering = !!(
    searchTerm ||
    filtersToApply?.type?.length ||
    filtersToApply?.contentType?.length ||
    filtersToApply?.populationType?.length ||
    filtersToApply?.classType?.length
  );

  const hasItems = totalRecords > 0 || emptyFolders.length > 0;

  // Number of folder groups (unique folders from touchpoints + empty folders + "Not in a Folder" if any).
  const folderCount = useMemo(() => {
    if (folderId) return 0;
    const folderIdsFromTouchpoints = new Set(
      touchpoints.filter((tp) => tp.folderId).map((tp) => tp.folderId!),
    );
    const folderIdsFromEmpty = new Set(emptyFolders.map((ef) => ef.groupValue));
    const uniqueFolderCount = new Set([...folderIdsFromTouchpoints, ...folderIdsFromEmpty]).size;
    const hasNotInFolder = touchpoints.some((tp) => !tp.folderId);
    return uniqueFolderCount + (hasNotInFolder ? 1 : 0);
  }, [touchpoints, emptyFolders, folderId]);

  // Show "Collapse all" when: all expanded (true), or object with at least one folder expanded.
  const showCollapseAll = useMemo(() => {
    if (folderId) return false;
    if (folderCount === 0) return true;

    if (typeof expandedFolderIds === "boolean") {
      return expandedFolderIds;
    }

    if (
      typeof expandedFolderIds === "object" &&
      expandedFolderIds !== null &&
      !Array.isArray(expandedFolderIds)
    ) {
      const obj = expandedFolderIds as Record<string, boolean>;
      return Object.entries(obj).some(
        ([key, value]) => key.startsWith("folderId:") && value === true,
      );
    }
    return false;
  }, [expandedFolderIds, folderCount]);

  const toggleExpandAll = () => {
    if (showCollapseAll) {
      setExpandedFolderIds({});
    } else {
      setExpandedFolderIds(true);
    }
  };

  return (
    <TableContainer fillHeight>
      {(!folderId || hasItems || isSearchOrFiltering) && (
        <TableSearchAndFilters
          setCurrentSearchTerm={setSearchTerm}
          filtersToApply={filtersToApply}
          setFiltersToApply={(next) => setFiltersToApply(next)}
          defaultFiltersToApply={DEFAULT_FILTERS_TO_APPLY}
          filterIdToTitle={FILTER_ID_TO_TITLE}
          tableFilters={
            <FiltersOnCreativeTable
              filtersToApply={filtersToApply}
              setFiltersToApply={(next) => setFiltersToApply(next)}
              tableColumnsConfig={tableColumnsConfig}
              columnOptions={columnOptions}
              onToggleColumn={handleToggleColumn}
              isFetching={isAnyFetching}
            />
          }
          actionSlot={actionSlot}
        />
      )}

      {folderCount > 1 && (
        <Button
          variant="text"
          onClick={toggleExpandAll}
          sx={EXPAND_ALL_BUTTON_SX}
          aria-label={showCollapseAll ? "Collapse all folders" : "Expand all folders"}
          endIcon={
            <Icons.ArrowRight
              customClass={`size-5 text-aqua-600 ${showCollapseAll ? "rotate-90" : ""}`}
            />
          }>
          {showCollapseAll ? "Collapse all" : "Expand all"}
        </Button>
      )}

      <TableFillHeightBody>
        <TableLoader isLoading={isInitialLoading} fullScreen />

        {!isInitialLoading &&
          (touchpoints.length || emptyFolders.length ? (
            <CreativeTableSelectionContext value={selection}>
              <DataGrid<TouchpointWithFolder, { folder: CampaignFolderDTO }>
                columns={columns}
                data={touchpointsForTable}
                pinPrimaryColumnLeft={pinPrimaryColumnLeft}
                emptyGroups={emptyFolders}
                getEmptyGroupNode={(emptyGroup) => (
                  <FolderTableRow
                    folder={emptyGroup.original.folder}
                    touchpointCount={0}
                    isExpanded={false}
                  />
                )}
                isLoading={isAnyFetching && !isInitialLoading}
                manualSorting={true}
                pagination={false}
                totalRecords={totalRecords}
                sortingParams={sorting}
                setSortingParams={setSorting}
                isExpandable={!folderId}
                expandedState={expandedFolderIds}
                onExpandedChange={setExpandedFolderIds}
                enableGrouping={!folderId}
                grouping={!folderId ? ["folderId"] : []}
                groupedColumnMode={!folderId ? "remove" : undefined}
                fillHeight
                styles={{
                  rowClassName: "h-[56px]",
                  cellClassName: "py-2",
                  groupRowClassName: "h-[36px]",
                  groupCellClassName: "py-0",
                  containerClassName: "overflow-x-auto",
                }}
                getGroupedNode={(row) => {
                  const visibleCount = row.subRows?.length ?? 0;
                  const totalInFolder =
                    row.original.folder != null
                      ? (row.original.folder.touchpointCount ?? undefined)
                      : totalNotInFolder;
                  const countLabel =
                    isSearchOrFiltering && totalInFolder != null
                      ? `${visibleCount}/${totalInFolder}`
                      : visibleCount;
                  return (
                    <FolderTableRow
                      folder={row.original.folder || null}
                      touchpointCount={visibleCount}
                      countLabel={countLabel}
                      isExpanded={row.getIsExpanded()}
                      onToggleExpanded={
                        row.subRows?.length ? row.getToggleExpandedHandler() : undefined
                      }
                    />
                  );
                }}
              />
            </CreativeTableSelectionContext>
          ) : (
            <TableItemsNotFound />
          ))}
      </TableFillHeightBody>
    </TableContainer>
  );
};

export default CreativeTable;
